import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { play } from '@/features/game/sound'
import { useReconnect } from '@/features/game/useConnection'
import { echo } from '@/shared/echo'
import { fetchMessages } from './api'
import { useMutedSeats } from './settings'
import type { ChatMessage } from './types'

export function chatKey(gameId: string): [string, string] {
  return ['chat', gameId]
}

/**
 * Дописать реплики к тому, что уже показано.
 *
 * По номеру, а не «в конец»: реплики приходят и событием, и догоном, и
 * порядок между этими двумя путями не гарантирован — своя нумерация внутри
 * партии для этого и есть. Повтор той же реплики отбрасывается: событие о
 * своей реплике не приходит, но чужая может приехать и событием, и добором.
 */
function merge(known: ChatMessage[] | undefined, coming: ChatMessage[]): ChatMessage[] {
  const seen = new Map((known ?? []).map((message) => [message.seq, message]))

  for (const message of coming) seen.set(message.seq, message)

  return [...seen.values()].sort((a, b) => a.seq - b.seq)
}

/**
 * Придержать реплики, которые называют то, что доска ещё не показала.
 *
 * Чат приходит своим событием (chat.posted) и рисуется сразу — с очередью
 * показа доски (usePlayback) он не связан никак. Реплика бота при этом
 * пишется сразу за его броском или ходом, а очередь ещё секунду доигрывает
 * саму сцену: без этой сверки "шестёрка" называла бы выпавшее раньше, чем
 * кубики долетели до глаз игрока.
 *
 * cursor — номер сцены, которую доска УЖЕ доиграла (usePlayback().cursor).
 * afterSeq у реплики — номер сцены, после которой её можно показывать;
 * null — реплика ни к чему не привязана (застольный разговор, свободный
 * текст, реплики до этой правки) и видна сразу.
 *
 * undefined проходит насквозь: это «список ещё не загрузился», и это
 * важно отличать от «загрузился и пуст» (см. GameScreen, useSpeech).
 */
export function reveal(
  messages: ChatMessage[] | undefined,
  cursor: number | null,
): ChatMessage[] | undefined {
  if (messages === undefined) return undefined

  return messages.filter(
    (message) => message.afterSeq === null || (cursor !== null && cursor >= message.afterSeq),
  )
}

/**
 * Разговор за столом.
 *
 * Свой ключ запроса и ДОПИСЫВАНИЕ из события, а не инвалидация партии:
 * событие chat.posted несёт реплику целиком (в отличие от game.updated, где
 * в сообщение не влезает состояние), и перезапрашивать партию на каждое
 * «ага» стоило бы дороже самой реплики.
 *
 * Догон — двумя путями, и оба обязательны: у канала нет истории, и разговор,
 * ушедший вперёд, пока сокет лежал или вкладка спала, задним числом не
 * приедет. Первый путь — возврат на вкладку (refetchOnWindowFocus), второй —
 * восстановление связи с Reverb (useReconnect).
 */
export function useChat(gameId: string, enabled: boolean, mine: number | null = null) {
  const client = useQueryClient()
  const muted = useMutedSeats(gameId)

  const query = useQuery({
    queryKey: chatKey(gameId),
    queryFn: () => fetchMessages(gameId),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    // Возврат на вкладку: пока она была неактивной, сокет мог отвалиться.
    refetchOnWindowFocus: 'always',
  })

  /** Забрать пропущенное — то, что новее последней известной реплики. */
  const catchUp = useCallback(async () => {
    const known = client.getQueryData<ChatMessage[]>(chatKey(gameId))
    const coming = await fetchMessages(gameId, known?.[known.length - 1]?.seq)

    if (coming.length > 0) {
      client.setQueryData<ChatMessage[]>(chatKey(gameId), (old) => merge(old, coming))
    }
  }, [client, gameId])

  useReconnect(() => {
    if (enabled) void catchUp()
  })

  useEffect(() => {
    if (!enabled) return

    const channel = echo.channel(`games.${gameId}`)
    const onPosted = (message: ChatMessage) => {
      client.setQueryData<ChatMessage[]>(chatKey(gameId), (old) => merge(old, [message]))
    }

    channel.listen('.chat.posted', onPosted)

    /*
     * Снимается только свой слушатель: канал общий с состоянием партии
     * (useGame), и уйти с него целиком значило бы отобрать события у неё.
     */
    return () => {
      channel.stopListening('.chat.posted', onPosted)
    }
  }, [gameId, client, enabled])

  /*
   * Звук — только на ЧУЖУЮ реплику и только на новую.
   *
   * Своя вернулась ответом на собственный запрос, и звонить о ней нечего.
   * Затыкание соперника (settings.ts) должно быть полным: и без строки,
   * и без звука.
   *
   * Отметка «до какой реплики уже звонили» — в ref, а не в состоянии: она
   * не влияет на отрисовку, а её изменение не должно вызывать рендер.
   */
  const rung = useRef<number | null>(null)
  const messages = query.data

  useEffect(() => {
    if (messages === undefined) return

    const last = messages[messages.length - 1]

    /*
     * Первая загрузка — это история, а не разговор: звонить на неё значит
     * здороваться десятком звуков при каждом входе в партию.
     *
     * Отметка ставится и на ПУСТОМ разговоре — нулём. Иначе «уже загрузились»
     * было бы неотличимо от «ещё нет», и первая реплика за только что
     * начатым столом проходила бы молча.
     */
    if (rung.current === null) {
      rung.current = last?.seq ?? 0

      return
    }

    if (last === undefined || last.seq <= rung.current) return

    rung.current = last.seq

    if (last.seat !== mine && !muted.includes(last.seat)) play('chat')
  }, [messages, muted, mine])

  return query
}
